import React from 'react';
import MediaPreview from './MediaPreview';
import { formatFileSize, formatDateTime, formatRelativeTime } from '../../../utils/formatters';

/**
 * Side-by-side version comparison view
 */
const CompareView = ({
  file,
  currentCompareVersion,
  currentPreviewData,
  comparePreviewData,
  compareIndex,
  olderVersions,
  totalVersions,
  olderVersionNumber,
  zoom,
  onPrevVersion,
  onNextVersion,
  onRestore,
  onRestoreClick,
}) => {
  // Navigation display: 1 = oldest, N = most recent older version
  const displayIndex = olderVersions.length - compareIndex;
  
  return (
    <>
      {/* OLDER VERSION (left side) */}
      <div className="preview-panel__compare-side preview-panel__compare-side--old">
        {/* Compact header */}
        <div className="preview-panel__side-header preview-panel__side-header--old">
          <div className="preview-panel__header-row">
            <span className="preview-panel__version-badge preview-panel__version-badge--old">
              v{olderVersionNumber}
            </span>
            <span className="preview-panel__header-ago">
              {compareIndex + 1} version{compareIndex + 1 !== 1 ? 's' : ''} ago
            </span>
            <span className="preview-panel__header-dot">•</span>
            <span className="preview-panel__header-time">
              {formatRelativeTime(currentCompareVersion.uploadedAt, file.uploadedAt)}
            </span>
          </div>
          <div className="preview-panel__header-details">
            <div className="preview-panel__detail-box">
              <span className="preview-panel__detail-label">Date</span>
              <span className="preview-panel__detail-value">
                {formatDateTime(currentCompareVersion.uploadedAt)}
              </span>
            </div>
            <div className="preview-panel__detail-box">
              <span className="preview-panel__detail-label">Size</span>
              <span className="preview-panel__detail-value">
                {formatFileSize(currentCompareVersion.size)}
              </span>
            </div>
            {currentCompareVersion.note && (
              <div className="preview-panel__detail-box preview-panel__detail-box--note">
                <span className="preview-panel__detail-label">Description</span>
                <span className="preview-panel__detail-value preview-panel__detail-value--note" title={currentCompareVersion.note}>
                  {currentCompareVersion.note}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Preview content */}
        <div className="preview-panel__side-content">
          <MediaPreview data={comparePreviewData} zoom={zoom} />
        </div>
        
        {/* Footer with navigation and restore */}
        <div className="preview-panel__side-footer preview-panel__side-footer--old">
          {/* Navigation */}
          <div className="preview-panel__version-nav">
            <button 
              className="preview-panel__nav-btn"
              onClick={onNextVersion}
              disabled={compareIndex === olderVersions.length - 1}
              title="Go to older version"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span className="preview-panel__nav-label">Older</span>
            </button>
            <span className="preview-panel__nav-indicator">
              {displayIndex} / {olderVersions.length}
            </span>
            <button 
              className="preview-panel__nav-btn"
              onClick={onPrevVersion}
              disabled={compareIndex === 0}
              title="Go to newer version"
            >
              <span className="preview-panel__nav-label">Newer</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* Restore button */}
          {onRestore && (
            <button 
              className="preview-panel__restore-btn"
              onClick={onRestoreClick}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              Restore
            </button>
          )}
        </div>
      </div>
      
      <div className="preview-panel__divider">
        <div className="preview-panel__divider-line" />
      </div>
      
      {/* CURRENT VERSION (right side) */}
      <div className="preview-panel__compare-side preview-panel__compare-side--current">
        {/* Compact header */}
        <div className="preview-panel__side-header preview-panel__side-header--current">
          <div className="preview-panel__header-row">
            <span className="preview-panel__version-badge preview-panel__version-badge--current">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              v{totalVersions}
            </span>
            <span className="preview-panel__header-current-label">Current</span>
          </div>
          <div className="preview-panel__header-details preview-panel__header-details--current">
            <div className="preview-panel__detail-box preview-panel__detail-box--current">
              <span className="preview-panel__detail-label preview-panel__detail-label--current">Date</span>
              <span className="preview-panel__detail-value preview-panel__detail-value--current">
                {formatDateTime(file.uploadedAt)}
              </span>
            </div>
            <div className="preview-panel__detail-box preview-panel__detail-box--current">
              <span className="preview-panel__detail-label preview-panel__detail-label--current">Size</span>
              <span className="preview-panel__detail-value preview-panel__detail-value--current">
                {formatFileSize(file.size)}
              </span>
            </div>
            {file.note && (
              <div className="preview-panel__detail-box preview-panel__detail-box--current preview-panel__detail-box--note">
                <span className="preview-panel__detail-label preview-panel__detail-label--current">Description</span>
                <span className="preview-panel__detail-value preview-panel__detail-value--current preview-panel__detail-value--note" title={file.note}>
                  {file.note}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Preview content */}
        <div className="preview-panel__side-content">
          <MediaPreview data={currentPreviewData} zoom={zoom} />
        </div>

        {/* Footer for alignment */}
        <div className="preview-panel__side-footer preview-panel__side-footer--current">
          <span className="preview-panel__current-footer-text">
            Currently in use
          </span>
        </div>
      </div>
    </>
  );
};

export default CompareView;
